import React, { FunctionComponent, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import {
    withStyles,
    Theme,
    createTheme,
    ThemeProvider,
} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import styles from './TargetGroupTable.module.css';
import ParticipantRow from './ParticipantRow';
import { Assessment } from '../../../typings/assessment';
import { Participant } from '../../../typings/participant';
import { ParticipantActivityWithoutReportUrl } from '../../../typings/participant_activity';
import ProgressSpinner from 'components/ProgressSpinner/ProgressSpinner';

interface TargetGroupTableProps {
    scan: Assessment;
    participants?: Participant[];
    participantsSelected: Participant[];
    hasNoParticipants: boolean;
    isUsersInfoLoading: boolean;
    onSelect: (userId: string, event: any) => void;
    onSelectAll: () => void;
    companyId: string;
    groupId: string;
    isSuperAdmin: boolean;
    groupHasOptOutQuestionnaire: boolean;
    isBatchProcessLoading: boolean;
    participantIdsBeingBatchProcessed: string[];
}

export const LightTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: '#435970',
        boxShadow: '0 0 3px 0 #435970',
        fontSize: 14,
        minWidth: 100,
        borderRadius: 8,
    },
}))(Tooltip);

export const theme = createTheme({
    overrides: {
        MuiTableHead: {
            root: {
                border: 'solid 1px #bbc9d9',
            },
        },
        MuiTableRow: {
            root: {
                '&$selected, &$selected:hover': {
                    backgroundColor: '#F6F9FD',
                },
            },
            head: { border: 'solid 1px #bbc9d9' },
        },
        MuiTableCell: {
            root: {
                '&$head': {
                    fontWeight: '700',
                    color: '#445870',
                    backgroundColor: '#F4F6FA',
                    borderRadius: 0,
                },
                lineHeight: '0.7',
                verticalAlign: 'middle',
            },
            sizeSmall: { padding: '5px' },
        },
        MuiTableContainer: {
            root: {
                maxHeight: 440,
            },
        },
    },
});

export const getLastCompletedSession = (
    userActivity?: ParticipantActivityWithoutReportUrl,
) => {
    if (
        !userActivity ||
        !userActivity.sessions ||
        userActivity.sessions.length === 0
    ) {
        return null;
    }
    const completedSessions = userActivity.sessions.filter(
        (session) => session.finishedAt,
    );

    if (completedSessions.length > 0) {
        completedSessions.sort((a, b) =>
            b.finishedAt.localeCompare(a.finishedAt),
        );
        return completedSessions[0];
    }

    return userActivity.sessions[userActivity.sessions.length - 1];
};

const TargetGroupTable: FunctionComponent<TargetGroupTableProps> = (
    props: TargetGroupTableProps,
) => {
    const {
        scan,
        companyId,
        groupId,
        participants,
        isSuperAdmin,
        participantsSelected,
        isUsersInfoLoading,
        hasNoParticipants,
        onSelect,
        onSelectAll,
        isBatchProcessLoading,
        participantIdsBeingBatchProcessed,
        groupHasOptOutQuestionnaire,
    } = props;
    const [usersShowingInfo, updateUsersShowingInfo] = useState<string[]>([]);
    const onUserShowInfoChange = (userId: string) => {
        const alreadyShowingInfo = usersShowingInfo.some((id) => id === userId);
        if (alreadyShowingInfo) {
            const update = [...usersShowingInfo].filter((id) => id !== userId);
            updateUsersShowingInfo(update);
        } else {
            const update = [...usersShowingInfo];
            // @ts-ignore
            update.push(userId);
            updateUsersShowingInfo(update);
        }
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={1} />
                                <TableCell>
                                    <Checkbox
                                        disabled={isBatchProcessLoading}
                                        indeterminate={
                                            !!participants &&
                                            participantsSelected.length > 0 &&
                                            participantsSelected.length <
                                                Object.keys(participants).length
                                        }
                                        checked={
                                            !!participants &&
                                            Object.keys(participants).length >
                                                0 &&
                                            Object.keys(participants).length ===
                                                participantsSelected.length
                                        }
                                        onChange={onSelectAll}
                                        classes={{
                                            checked: styles.checkedBox,
                                            indeterminate: styles.checkedBox,
                                        }}
                                        color="primary"
                                    />
                                </TableCell>
                                <TableCell align="left">First name</TableCell>
                                <TableCell align="left">Last name</TableCell>
                                <TableCell align="left">E-mail</TableCell>
                                <TableCell align="center">Registered</TableCell>
                                <TableCell align="center">
                                    Scan status
                                </TableCell>
                                <TableCell align="center">Report</TableCell>
                                <TableCell align="center">
                                    Report generation date
                                </TableCell>
                                <TableCell align="right">More info</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!isUsersInfoLoading && hasNoParticipants && (
                                <TableRow>
                                    <TableCell align="left" colSpan={10}>
                                        <div
                                            className={
                                                styles.no_candidates_yet
                                            }>
                                            <em>
                                                There are no candidates yet.
                                            </em>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {isUsersInfoLoading && (
                                <TableRow>
                                    <TableCell align="center" colSpan={10}>
                                        <ProgressSpinner />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isUsersInfoLoading &&
                                !!participants &&
                                participants.length > 0 &&
                                participants.map((participant) => {
                                    return (
                                        <ParticipantRow
                                            key={`participant-row_${participant.user_id}`}
                                            participant={participant}
                                            scan={scan}
                                            groupId={groupId}
                                            companyId={companyId}
                                            groupHasOptOutQuestionnaire={
                                                groupHasOptOutQuestionnaire
                                            }
                                            participantsSelected={
                                                participantsSelected
                                            }
                                            usersShowingInfo={usersShowingInfo}
                                            isBatchProcessLoading={
                                                isBatchProcessLoading &&
                                                !!participantIdsBeingBatchProcessed.find(
                                                    (userId) =>
                                                        userId ===
                                                        participant.user_id,
                                                )
                                            }
                                            onUserShowInfoChange={
                                                onUserShowInfoChange
                                            }
                                            invitationId={
                                                participant.invitation_id
                                            }
                                            isUsersDetailsLoading={
                                                isUsersInfoLoading
                                            }
                                            isSuperAdmin={isSuperAdmin}
                                            onSelect={onSelect}
                                        />
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ThemeProvider>
        </>
    );
};

export default TargetGroupTable;
